import { useCallback, useEffect, useState } from "react";
import { Label } from "@project/components/ui/label";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "@project/admin/components/searchable-select";
import {
  EMPTY_LOCATION_LABELS,
  locationSeedOption,
  resolveLocationLabels,
  searchAreaOptions,
  searchCityOptions,
  searchDistrictOptions,
} from "@project/components/location-cascading-selects";
import type { DonorFieldErrors } from "@project/donate/components/donor-validation";
import { cn } from "@project/lib/utils";

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}

export type DonationLocationValue = {
  district_id: string;
  city_id: string;
  area_id: string;
};

type DonationLocationFieldsProps = {
  value: DonationLocationValue;
  onChange: (value: DonationLocationValue) => void;
  errors?: Pick<DonorFieldErrors, "district" | "city" | "area">;
};

export function DonationLocationFields({
  value,
  onChange,
  errors = {},
}: DonationLocationFieldsProps) {
  const [labels, setLabels] = useState(EMPTY_LOCATION_LABELS);

  useEffect(() => {
    let ignore = false;
    void resolveLocationLabels(
      {
        districtId: value.district_id,
        cityId: value.city_id,
        areaId: value.area_id,
      },
      EMPTY_LOCATION_LABELS
    ).then(next => {
      if (!ignore) {
        setLabels(next);
      }
    });
    return () => {
      ignore = true;
    };
  }, [value.district_id, value.city_id, value.area_id]);

  const searchDistricts = useCallback(
    (query: string) => searchDistrictOptions(query),
    []
  );

  const searchCities = useCallback(
    (query: string) => searchCityOptions(value.district_id, query),
    [value.district_id]
  );

  const searchAreas = useCallback(
    (query: string) => searchAreaOptions(value.city_id, query),
    [value.city_id]
  );

  const handleDistrictChange = (
    district_id: string,
    option?: SearchableSelectOption
  ) => {
    onChange({ district_id, city_id: "", area_id: "" });
    setLabels({
      districtName: option?.label ?? "",
      cityName: "",
      areaName: "",
    });
  };

  const handleCityChange = (
    city_id: string,
    option?: SearchableSelectOption
  ) => {
    onChange({ ...value, city_id, area_id: "" });
    setLabels(prev => ({
      ...prev,
      cityName: option?.label ?? "",
      areaName: "",
    }));
  };

  const handleAreaChange = (
    area_id: string,
    option?: SearchableSelectOption
  ) => {
    onChange({ ...value, area_id });
    setLabels(prev => ({
      ...prev,
      areaName: option?.label ?? "",
    }));
  };

  const invalidClass = (hasError?: string) =>
    cn(hasError && "border-destructive focus-visible:ring-destructive");

  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="min-w-0">
        <Label data-required="true">District</Label>
        <SearchableSelect
          value={value.district_id}
          onValueChange={handleDistrictChange}
          onSearch={searchDistricts}
          seedOption={locationSeedOption(
            value.district_id,
            labels.districtName
          )}
          placeholder="Select district…"
          searchPlaceholder="Search district…"
          emptyText="No district found."
          className={invalidClass(errors.district)}
        />
        <FieldError message={errors.district} />
      </div>
      <div className="min-w-0">
        <Label data-required="true">City</Label>
        <SearchableSelect
          value={value.city_id}
          onValueChange={handleCityChange}
          onSearch={searchCities}
          seedOption={locationSeedOption(value.city_id, labels.cityName)}
          placeholder={
            value.district_id ? "Select city…" : "Select district first"
          }
          searchPlaceholder="Search city…"
          emptyText={
            value.district_id ? "No city found." : "Select a district first."
          }
          disabled={!value.district_id}
          className={invalidClass(errors.city)}
        />
        <FieldError message={errors.city} />
      </div>
      <div className="min-w-0">
        <Label data-required="true">Area</Label>
        <SearchableSelect
          value={value.area_id}
          onValueChange={handleAreaChange}
          onSearch={searchAreas}
          seedOption={locationSeedOption(value.area_id, labels.areaName)}
          placeholder={value.city_id ? "Select area…" : "Select city first"}
          searchPlaceholder="Search area…"
          emptyText={value.city_id ? "No area found." : "Select a city first."}
          disabled={!value.city_id}
          className={invalidClass(errors.area)}
        />
        <FieldError message={errors.area} />
      </div>
    </div>
  );
}
